import { useParams } from 'react-router-dom'

import { GetTasksDataTableDocument } from 'generated/resources/datatable.graphql'
import {
  GetTaskDetailDocument,
  GetTaskDetailsDocument,
  GetTaskFilesDocument,
  ModalTaskFragment,
  TaskPageDocument,
  TasksValueObjectsTaskIdentifierEnum,
  UpdateProjectTaskDocument,
  UpdateTaskDocument,
} from 'generated/resources/graphql'

import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useForm } from 'modules/core/hooks/useForm'
import { useMutation } from 'modules/core/hooks/useMutation'
import { useLazyQuery, useQuery } from 'modules/core/hooks/useQuery'
import { Result, handleResult } from 'modules/core/lib/handleMutation'
import i18n from 'modules/core/lib/i18n'
import app from 'modules/core/store/models/app'

import TaskForm, { ModalTaskWithFiles } from 'modules/tasks/components/TaskForm'
import { analyticsVariables } from 'modules/tasks/components/TasksList/components/TaskAnalytics/analyticsData'
import { useCreateTaskFile } from 'modules/tasks/hooks/useCreateTaskFile'
import { useDeleteTaskFile } from 'modules/tasks/hooks/useDeleteTaskFile'
import { useFetchTaskFiles } from 'modules/tasks/hooks/useFetchTaskFiles'
import tasksFiles, { TasksFiles } from 'modules/tasks/store/collections/tasks_files'
import type TaskFile from 'modules/tasks/store/models/TaskFile'

interface Props {
  taskId: number
  onClose: () => void
}

interface FormProps {
  task: ModalTaskFragment
  onClose: () => void
}

const FormWrapper = ({ onClose, task }: FormProps) => {
  const {
    access: { id: accessId },
    company: { id: companyId },
  } = useCurrentSession()
  const { createTaskFile, loading: creatingTaskFile } = useCreateTaskFile()
  const { deleteTaskFile, loading: deletingTaskFile } = useDeleteTaskFile()
  const { identifier } = useParams()
  const taskIdentifier = identifier as TasksValueObjectsTaskIdentifierEnum // eslint-disable-line no-type-assertion/no-type-assertion

  const [fetchTask] = useLazyQuery(GetTaskDetailsDocument, {
    variables: {
      ids: [task.id],
      identifier: taskIdentifier,
      companyId,
    },
  })

  const [fetchTaskPageData] = useLazyQuery(TaskPageDocument, {
    variables: analyticsVariables(accessId),
  })

  const [fetchDataTask] = useLazyQuery(GetTasksDataTableDocument, {
    variables: {
      ids: [task.id],
      identifier: taskIdentifier,
    },
  })

  const [updateTask, { loading: updatingTask }] = useMutation(UpdateTaskDocument, {
    extractorFn: (data) => data.tasksMutations.updateTasksTask,
    refetchQueries: [GetTaskFilesDocument, GetTaskDetailDocument],
  })

  const [updateProjectTask, { loading: updatingProjectTask }] = useMutation(
    UpdateProjectTaskDocument,
    {
      extractorFn: (data) => data.projectManagementMutations.updateProjectmanagementProjectTask,
      refetchQueries: [GetTaskFilesDocument, GetTaskDetailDocument],
    }
  )

  const form = useForm<ModalTaskWithFiles>({
    defaultValues: {
      name: task.name,
      assignees: task.assignees,
      author: task.author,
      content: task.content || '',
      id: task.id,
      dueOn: task.dueOn || null,
      newFiles: new TasksFiles(),
      removeFiles: new TasksFiles(),
      subprojectId: task.projectTask?.subproject?.id || undefined,
    },

    onSubmit: async (data, { setErrors }) => {
      const projectEdited = data.projectId !== task.projectTask?.imputableProject?.id
      const subprojectEdited = data.subprojectId !== task.projectTask?.subproject?.id

      const projectTaskEdited = projectEdited || subprojectEdited
      if (
        projectTaskEdited &&
        task.projectTask &&
        task.projectTask.imputableProject &&
        task.status
      ) {
        const result = await updateProjectTask({
          variables: {
            id: task.projectTask.id,
            assigneeEmployeeIds: data.assignees.nodes
              .map((a) => a.employeeName?.id)
              .filter(Boolean),
            content: data.content,
            dueOn: data.dueOn || null,
            filesToAdd: data.newFiles.toArray().map((file) => file.attributes.file),
            filesToRemove: data.removeFiles.toArray().map((file) => Number(file.attributes.id)),
            name: data.name,
            projectId: data.projectId || task.projectTask.imputableProject.id,
            status: task.status,
            subprojectId: data.subprojectId || null,
          },
        })

        const handled = handleResult(result, (errors) => errors && setErrors(errors))
        handleResponse(handled)

        await Promise.all([fetchTask(), fetchDataTask(), fetchTaskPageData()])
        handled.success && onClose()
        return handled
      } else {
        const result = await updateTask({
          variables: {
            id: data.id,
            name: data.name,
            assigneeIds: data.assignees.nodes.map((a) => a.employeeName?.access.id).filter(Boolean),
            content: data.content,
            dueOn: data.dueOn || null,
          },
        })

        const handled = handleResult(result, (errors) => errors && setErrors(errors))
        handleResponse(handled)

        await Promise.all([fetchTask(), fetchDataTask(), fetchTaskPageData()])
        handled.success && onClose()
        return handled
      }
    },
  })

  async function saveFileWithTask(file: TaskFile, taskId: number) {
    const result = await createTaskFile({
      taskId,
      file: file.attributes.file,
    })
    handleResult(result, (errors) => {
      errors && form.setErrors(errors)
    })
  }

  async function deleteFileWithTask(fileId: number) {
    const result = await deleteTaskFile({
      id: fileId,
    })
    handleResult(result, (errors) => {
      errors && form.setErrors(errors)
    })
  }

  const deleteFiles = async (removeFiles: TasksFiles, newFiles: TasksFiles) => {
    await Promise.all([
      ...removeFiles.map((file) => deleteFileWithTask(Number(file.attributes.id))),
      ...newFiles.map((file) => saveFileWithTask(file, task.id)),
    ])
    tasksFiles.add(newFiles.toArray())
    tasksFiles.remove(removeFiles.map((f) => f.attributes.id))
  }

  const canEdit = task._policies?.edit

  const loadingFiles = useFetchTaskFiles(tasksFiles, accessId, task.id)

  const handleResponse = (result: Result<unknown>) => {
    if (result.success) {
      app.setFlash({ type: 'correct', message: i18n.t('common.saved_successfully') })
    } else {
      app.setFlash({ type: 'wrong', message: i18n.t('common.save_error') })
    }
  }

  return (
    <TaskForm
      mode="edit"
      form={form}
      submitting={updatingTask || creatingTaskFile || deletingTaskFile || updatingProjectTask}
      close={onClose}
      onSubmit={async (removeFiles, newFiles) => {
        form.handleSubmit()
        await deleteFiles(removeFiles, newFiles)
      }}
      readonly={!canEdit}
      files={tasksFiles as any}
      loading={loadingFiles}
      projectSelected={task.projectTask?.imputableProject}
      subprojectSelected={task.projectTask?.subproject || null}
    />
  )
}

const EditTaskModal = ({ taskId, onClose }: Props) => {
  const { data } = useQuery(GetTaskDetailDocument, {
    variables: { id: taskId },
  })
  const task = data?.tasks.task

  if (!task) return null

  return <FormWrapper task={task} onClose={onClose} />
}

export default EditTaskModal
